import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import StaffLayout from '../../components/StaffLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useApi } from '../../lib/api';
import { STAFF_TABS, TYPE_LABELS, money, can, transactionClass, currencySymbol } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Search, Trash2 } from 'lucide-react';
import { CurrencyBalances } from '../../components/CurrencyBalances';
import { CurrencySwitcher, useStaffCurrency } from '../../components/CurrencySwitcher';
import { formatLocalDateTime } from '../../lib/datetime';
import { FinancialDeleteDialog } from '../../components/FinancialDeleteDialog';
import { FinancialDeletionHistory } from '../../components/FinancialDeletionHistory';

export function JournalReportPage() {
  const [currency, setCurrency] = useStaffCurrency();
  const { user } = useAuth();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState(null);
  const { data: journal, reload } = useApi(`/transactions?limit=2000&currency=${currency}`);
  const canDelete = can(user, 'deleteTransactions');
  const filtered = (journal || []).filter(j => !q || j.refNo.toLowerCase().includes(q.toLowerCase()) || (j.name || '').toLowerCase().includes(q.toLowerCase()) || j.accountNo.includes(q));
  return (
    <StaffLayout tabs={STAFF_TABS}>
      <Card className="border-slate-200"><CardContent className="p-0">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-semibold text-slate-800">Journal Report</div>
          <CurrencySwitcher currency={currency} onChange={setCurrency} />
          {user.role === 'Admin' && <FinancialDeletionHistory />}
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="journal-report-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search ref, name, account" className="pl-9" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="journal-report-table">
            <thead className="bg-slate-700 text-white">
              <tr>{['Ref No', 'Account No', 'Name', 'Type', 'Debit', 'Credit', 'Ccy', 'Date', 'User', 'Status', ''].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wide font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((j, i) => (
                <tr key={j.id} className={i % 2 ? 'bg-emerald-50/40' : 'bg-white'}>
                  <td className="px-4 py-2.5 font-mono text-xs">{j.refNo}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{j.accountNo}</td>
                  <td className="px-4 py-2.5">{j.name}</td>
                  <td data-testid={`report-type-${j.id}`} className={`px-4 py-2.5 ${transactionClass(j.type)}`}>{j.paymentKind === 'cash_withdrawal' ? 'QR Cash Withdrawal' : TYPE_LABELS[j.type] || j.type}</td>
                  <td data-testid={`report-debit-${j.id}`} className={`px-4 py-2.5 font-mono ${transactionClass(j.type)}`}>{money(j.debit)}</td>
                  <td data-testid={`report-credit-${j.id}`} className={`px-4 py-2.5 font-mono ${transactionClass(j.type)}`}>{money(j.credit)}</td>
                  <td className="px-4 py-2.5">{j.currency}</td>
                  <td data-testid={`report-date-${j.id}`} className="px-4 py-2.5 text-xs text-slate-600">{formatLocalDateTime(j.date)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{j.userName}</td>
                  <td className="px-4 py-2.5 text-xs capitalize">{j.status}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <Link data-testid={`report-receipt-${j.id}`} to={`/app/receipts/${j.id}`} className="text-emerald-700 text-xs font-medium">Receipt</Link>
                    {canDelete && !j.immutablePayment && !j.counterpartyDeleted && (user.role === 'Admin' || (!j.loanId && !j.loanAllocations?.length && !j.adjusted)) && <button data-testid={`report-delete-${j.id}`} title="Delete entry" aria-label="Delete entry" onClick={() => setDeleting({ resource: 'transactions', id: j.id })} className="ml-3 text-rose-600 hover:text-rose-700 align-middle"><Trash2 className="h-4 w-4 inline" /></button>}
                  </td>
                </tr>
              ))}
              {journal && filtered.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500">No transactions</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent></Card>
      {deleting && <FinancialDeleteDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); reload(); toast({ title: 'Deleted. Audit record retained.' }); }} />}
    </StaffLayout>
  );
}

export function BalanceSummaryPage() {
  const [currency, setCurrency] = useStaffCurrency();
  const { user } = useAuth();
  const allowed = can(user, 'balanceSummary');
  const { data: customers } = useApi(allowed ? '/reports/balance-summary' : '/shop');
  if (!allowed) return <Navigate to="/app/dashboard" replace />;
  const rows = Array.isArray(customers) ? customers : [];
  const total = { [currency]: rows.reduce((sum, row) => sum + Number(row.balances?.[currency] || 0), 0) };
  return (
    <StaffLayout tabs={STAFF_TABS}>
      <Card className="border-slate-200"><CardContent className="p-0">
        <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap gap-3 items-center justify-between">
          <div className="font-semibold text-slate-800">Balance Summary</div>
          <CurrencySwitcher currency={currency} onChange={setCurrency} />
          <div className="text-sm text-slate-600">Total held<CurrencyBalances currency={currency} balances={total} prefix="balance-summary-total" className="font-bold text-emerald-700" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="balance-summary-table">
            <thead className="bg-slate-700 text-white"><tr>{['Account', 'Name', currency, ''].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs uppercase font-semibold">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? 'bg-emerald-50/40' : 'bg-white'}>
                <td className="px-4 py-2.5 font-mono text-xs">{r.accountNo}</td>
                <td className="px-4 py-2.5">{r.name}</td>
                <td data-testid={`summary-${currency.toLowerCase()}-${r.accountNo}`} className="px-4 py-2.5 font-mono text-emerald-700">{currencySymbol(currency)}{money(r.balances?.[currency])}</td>
                <td className="px-4 py-2.5"><Link data-testid={`summary-statement-${r.accountNo}`} to={`/app/statement?accountNo=${r.accountNo}`} className="text-emerald-700 text-xs font-medium">Statement</Link></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </CardContent></Card>
    </StaffLayout>
  );
}
